import type { AssistantResponse } from '../types';
import { el } from './renderers/dom';
import { renderMap } from './renderers/renderMap';
import { renderMixed } from './renderers/renderMixed';
import { renderText } from './renderers/renderText';
import { renderTranscript as renderTranscriptEl } from './renderers/renderTranscript';
import { renderWeather } from './renderers/renderWeather';
import type { MapData, MixedData, WeatherDisplayData } from './renderers/types';

const OWNED_MARKER = 'kia-assistant-output';
/** Set on the output element to the current response's display.type — lets a publisher adjust their own surrounding graphics purely via a CSS attribute selector (e.g. `#kia-output[data-kia-display-type="map"] { ... }`), the same pattern as the mic element's data-kia-listening. */
const DISPLAY_TYPE_ATTR = 'data-kia-display-type';

/** Fired right after the matching widget's DOM has been built — one optional handler per widget type, all optional. */
export interface WidgetRenderHandlers {
  onMapRender?: (data: MapData) => void;
  onWeatherRender?: (data: WeatherDisplayData) => void;
}

/**
 * Owns the configured output container end to end. Every render() call
 * fully replaces the previous output (no stale iframes/nodes survive), and
 * every renderer this dispatches to builds DOM via textContent/attributes
 * only — never innerHTML, so nothing Gemini/a tool returns can execute as
 * markup or script.
 *
 * The renderer only ever displays already-normalized structured data — it
 * never decides *what* a response should contain (map vs weather vs text);
 * that judgment was made by Gemini/the tool layer upstream.
 */
export class OutputRenderer {
  private lastText: string | null = null;
  private lastLanguage: string | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly widgetHandlers?: WidgetRenderHandlers,
  ) {
    this.container.dataset.owner = OWNED_MARKER;
  }

  /**
   * Step 1 of 2: shows what the user was understood to have said,
   * immediately, before the answer exists yet — so the two appear in
   * conversational order instead of popping in together. Replaces whatever
   * was there before, same as render()/clear().
   */
  renderTranscript(transcript: string): void {
    this.clear();
    if (transcript) {
      renderTranscriptEl(this.container, transcript);
    }
  }

  /**
   * Step 2 of 2: reveals the answer itself, appended after whatever
   * renderTranscript() already put there. Call this once the response is
   * ready — the SDK controls the pause between the two steps.
   */
  renderResponse(response: AssistantResponse): void {
    // Set before any type-specific rendering so TTS always gets the
    // canonical text/language, regardless of what (if anything) fails below.
    this.lastText = response.message.text;
    this.lastLanguage = response.message.language;

    const type = response.display.type;
    this.container.setAttribute(DISPLAY_TYPE_ATTR, type);

    if (type === 'mixed') {
      renderMixed(this.container, response.data as MixedData);
      return;
    }

    if (type === 'text') {
      renderText(this.container, response.message.text);
      return;
    }

    // map/weather always come with the canonical text too — laid out
    // side by side (text left, widget right) rather than stacked, so the
    // response reads as one line instead of the widget just trailing below.
    // Percentage-based flex-basis (not a fixed px minimum) so this actually
    // fits real ad-unit widths, which are often only ~300px wide — a fixed
    // px basis (e.g. 220px + 320px) would just force a permanent wrap to
    // stacked in anything narrower than ~550px, defeating the point.
    // min-width: 0 on both is required too: flexbox's default min-width is
    // "auto" (the content's own min-content size), which otherwise silently
    // overrides flex-shrink and forces a wrap regardless of basis.
    //
    // The gap and both flex-basis values are CSS custom properties (with
    // these values as their fallback) rather than bare numbers — a
    // publisher can override them from their own stylesheet, e.g.
    // `#kia-output { --kia-response-widget-basis: 65%; }`, with no
    // !important needed, since the fallback only applies when the variable
    // is unset. See "Styling" in README.md for the full list.
    const row = el({ tag: 'div', className: 'kia-response-row' });
    row.style.display = 'flex';
    row.style.flexWrap = 'wrap';
    row.style.alignItems = 'flex-start';
    row.style.gap = 'var(--kia-row-gap, 8px)';

    const textColumn = el({ tag: 'div', className: 'kia-response-text-column' });
    textColumn.style.flexGrow = '1';
    textColumn.style.flexShrink = '1';
    textColumn.style.flexBasis = 'var(--kia-response-text-basis, 40%)';
    textColumn.style.minWidth = '0';
    renderText(textColumn, response.message.text);

    const widgetColumn = el({ tag: 'div', className: 'kia-response-widget-column' });
    widgetColumn.style.flexGrow = '0';
    widgetColumn.style.flexShrink = '1';
    widgetColumn.style.flexBasis = 'var(--kia-response-widget-basis, 50%)';
    widgetColumn.style.minWidth = '0';
    widgetColumn.style.marginLeft = 'auto';

    switch (type) {
      case 'map': {
        const data = response.data as MapData;
        renderMap(widgetColumn, data);
        this.widgetHandlers?.onMapRender?.(data);
        break;
      }
      case 'weather': {
        const data = response.data as WeatherDisplayData;
        renderWeather(widgetColumn, data);
        this.widgetHandlers?.onWeatherRender?.(data);
        break;
      }
    }

    row.append(textColumn, widgetColumn);
    this.container.append(row);
  }

  /** Convenience: both steps back to back, no pause — same net result as before this was split in two. */
  render(response: AssistantResponse): void {
    this.renderTranscript(response.transcript);
    this.renderResponse(response);
  }

  /** The most recent final response text, used by AudioOutput for TTS. Never altered by display-type-specific rendering. */
  getLastText(): string | null {
    return this.lastText;
  }

  /** The BCP-47 language tag for getLastText(), so TTS can pick a matching voice. */
  getLastLanguage(): string | null {
    return this.lastLanguage;
  }

  clear(): void {
    this.container.replaceChildren();
    this.container.removeAttribute(DISPLAY_TYPE_ATTR);
    this.lastText = null;
    this.lastLanguage = null;
  }

  destroy(): void {
    this.clear();
    delete this.container.dataset.owner;
  }
}
